import React from 'react';

import { formatDate, formatNombre } from '../../lib/formats';
import { libelle, texteSur } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';

const nombre = (valeur) => formatNombre(valeur, 0, '—');

/**
 * « Autres dépenses du camion »: frais du camion à la charge du transporteur,
 * hors trajet (entretien, papiers, écritures du journal). Chaque dépense se
 * coche; une fois déduite sur un état, elle n'est plus proposée ailleurs.
 *
 * `lecture`: l'état est validé ou annulé, la liste est celle qu'il porte.
 */
export const AutresDepenses = ({ depenses, choisies, disabled = false, lecture = false, onBasculer }) => {
  if (!depenses || depenses.length === 0) return null;
  const total = depenses
    .filter((depense) => lecture || choisies.has(depense.charge_key))
    .reduce((somme, depense) => somme + (Number(depense.amount) || 0), 0);

  return (
    <section className="space-y-2" aria-label="Autres dépenses du camion" data-testid="etat-autres-depenses">
      <h2 className="text-sm font-bold text-foreground">Autres dépenses du camion</h2>
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              {!lecture ? <TableHead className="w-8" /> : null}
              <TableHead>Date</TableHead>
              <TableHead>Tracteur</TableHead>
              <TableHead>Dépense</TableHead>
              <TableHead>Origine</TableHead>
              <TableHead className="text-right">Montant</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {depenses.map((depense) => {
              const choisie = lecture || choisies.has(depense.charge_key);
              return (
                <TableRow key={depense.charge_key} data-state={choisie && !lecture ? 'selected' : undefined}>
                  {!lecture ? (
                    <TableCell>
                      <Checkbox
                        checked={choisie}
                        disabled={disabled}
                        onCheckedChange={() => onBasculer(depense.charge_key)}
                        aria-label={`Déduire la dépense ${depense.label || ''}`.trim()}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDate(depense.date)}</TableCell>
                  <TableCell className="whitespace-nowrap font-semibold">{depense.tractor_plate || '—'}</TableCell>
                  <TableCell>{texteSur(depense.label, libelle('categorie_frais', depense.category))}</TableCell>
                  <TableCell>
                    <Badge variant="muted">{libelle('source_charge_proprietaire', depense.source)}</Badge>
                  </TableCell>
                  <TableCell
                    className={cn('whitespace-nowrap text-right tabular-nums', !choisie && 'text-muted-foreground')}
                  >
                    {nombre(depense.amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold">
              <TableCell colSpan={lecture ? 4 : 5}>Total déduit</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums" data-testid="etat-total-depenses">
                {nombre(total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </section>
  );
};

/**
 * « Avances »: paiements déjà faits au transporteur et pas encore affectés.
 * Une avance cochée se déduit du net à payer, pour un montant au plus égal à
 * son disponible.
 */
export const Avances = ({ avances, saisie, disabled = false, lecture = false, onBasculer, onMontant }) => {
  if (!avances || avances.length === 0) return null;
  const total = avances.reduce((somme, avance) => {
    const valeur = lecture ? avance.amount : saisie.avances[avance.payment_id];
    return somme + (Number(valeur) || 0);
  }, 0);

  return (
    <section className="space-y-2" aria-label="Avances" data-testid="etat-avances">
      <h2 className="text-sm font-bold text-foreground">Avances</h2>
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              {!lecture ? <TableHead className="w-8" /> : null}
              <TableHead>Date</TableHead>
              <TableHead>Observations</TableHead>
              {!lecture ? <TableHead className="text-right">Disponible</TableHead> : null}
              <TableHead className="text-right">Déduit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {avances.map((avance) => {
              const choisie = lecture || saisie.avances[avance.payment_id] !== undefined;
              return (
                <TableRow key={avance.payment_id} data-state={choisie && !lecture ? 'selected' : undefined}>
                  {!lecture ? (
                    <TableCell>
                      <Checkbox
                        checked={choisie}
                        disabled={disabled}
                        onCheckedChange={() => onBasculer(avance)}
                        aria-label={`Déduire l’avance du ${formatDate(avance.date)}`}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDate(avance.date)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{texteSur(avance.note, '')}</TableCell>
                  {!lecture ? (
                    <TableCell className="whitespace-nowrap text-right tabular-nums">{nombre(avance.available)}</TableCell>
                  ) : null}
                  <TableCell className="text-right">
                    {lecture ? (
                      <span className="whitespace-nowrap tabular-nums">{nombre(avance.amount)}</span>
                    ) : choisie ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max={avance.available}
                        step="any"
                        value={saisie.avances[avance.payment_id]}
                        disabled={disabled}
                        onChange={(event) => onMontant(avance, event.target.value)}
                        aria-label={`Montant déduit de l’avance du ${formatDate(avance.date)}`}
                        className="ml-auto h-8 w-28 text-right tabular-nums"
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="font-bold">
              <TableCell colSpan={lecture ? 2 : 4}>Total déduit</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums" data-testid="etat-total-avances">
                {nombre(total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </section>
  );
};
